import { endOfDay } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus16 } from 'shared/assets/icons';
import { InboxCount } from 'shared/components/connected/inbox-count';
import { LifeAreasList } from 'shared/components/connected/life-areas-list';
import {
  CreateSmartListSideNavigationItem,
  SmartListsList,
} from 'shared/components/connected/smart-lists';
import { TodayCount } from 'shared/components/connected/today-count';
import { Collapse } from 'shared/components/ui/collapse';
import { DisclosureButton } from 'shared/components/ui/disclosure-button';
import { IconButton, Sizes } from 'shared/components/ui/icon-button';
import { NavigationSectionLayout } from 'shared/components/ui/navigation-section-layout';
import { SideNavigationItem } from 'shared/components/ui/side-navigation-item';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import {
  MainNavigationItemId,
  SubNavigationItem,
} from 'shared/routes/navigation';
import { Paths } from 'shared/routes/paths';

import * as Styled from './side-navigation.style';
import { useSubItemDisclosure } from './use-sub-item-disclosure';

type SubItemProps = SubNavigationItem & {
  activePath?: Paths;
  onClick: () => void;
};

export const SubItem: React.FC<SubItemProps> = ({
  link,
  id,
  pathsToMatch,
  comingSoon,
  icon,
  onClick,
  activePath,
  translationLabel,
}) => {
  const { t } = useTranslation();
  const disclosure = useSubItemDisclosure(id);

  const active = !!activePath && !!pathsToMatch?.includes(activePath);
  const label = t(translationLabel);

  const openCreateTask = useOpenCreateTask();
  const today = useToday();

  const action = useMemo(() => {
    if (id === MainNavigationItemId.Inbox) {
      const onCreateTask = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        openCreateTask({});
      };

      return (
        <IconButton onClick={onCreateTask} icon={Plus16} size={Sizes.Small} />
      );
    }

    if (id === MainNavigationItemId.Today) {
      const onCreateTaskToday = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        openCreateTask({ endStrategy: { deadline: endOfDay(today) } });
      };

      return (
        <IconButton
          onClick={onCreateTaskToday}
          icon={Plus16}
          size={Sizes.Small}
        />
      );
    }
  }, [id, openCreateTask, today]);

  const end = useMemo(() => {
    if (!disclosure?.hasChildren) {
      return undefined;
    }

    const onToggle = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      disclosure.onToggle();
    };

    return (
      <DisclosureButton
        open={disclosure.open}
        onToggle={onToggle}
        label={label}
      />
    );
  }, [disclosure, label]);

  return (
    <>
      <SideNavigationItem
        label={label}
        onClick={onClick}
        link={link!}
        active={active}
        icon={icon}
        action={action}
        end={end}
      >
        {comingSoon && (
          <Styled.ComingSoon>
            {t('general.labels.coming-soon')}
          </Styled.ComingSoon>
        )}

        {id === MainNavigationItemId.Today && <TodayCount />}
        {id === MainNavigationItemId.Inbox && <InboxCount />}
      </SideNavigationItem>

      {id === MainNavigationItemId.AllTasks && (
        <Collapse isOpen={!!disclosure?.open} unmountOnExit={false}>
          <Styled.NestedListContainer>
            <SmartListsList />
            <CreateSmartListSideNavigationItem />
          </Styled.NestedListContainer>
        </Collapse>
      )}

      {id === MainNavigationItemId.LifeAreas && (
        <Collapse isOpen={!!disclosure?.open} unmountOnExit={false}>
          <Styled.NestedListContainer>
            <LifeAreasList />
          </Styled.NestedListContainer>
        </Collapse>
      )}
    </>
  );
};

export type NavigationSectionProps = {
  translationLabel: string;
  initialOpen?: boolean;
  subItems: SubNavigationItem[];
  onNavigationItem: () => void;
  activePath?: Paths;
  isFirst?: boolean;
};

export const NavigationSection: React.FC<NavigationSectionProps> = ({
  translationLabel,
  initialOpen,
  subItems,
  onNavigationItem,
  activePath,
  isFirst,
}) => {
  const { t } = useTranslation();

  return (
    <NavigationSectionLayout
      label={t(translationLabel)}
      isFirst={isFirst}
      initialOpen={initialOpen}
    >
      <Styled.SubNavigationList>
        {subItems.map((subItem) => (
          <Styled.SubNavigationListItem key={subItem.id}>
            <SubItem
              id={subItem.id}
              link={subItem.link}
              activePath={activePath}
              pathsToMatch={subItem.pathsToMatch}
              translationLabel={subItem.translationLabel}
              icon={subItem.icon}
              comingSoon={subItem.comingSoon}
              onClick={onNavigationItem}
            />
          </Styled.SubNavigationListItem>
        ))}
      </Styled.SubNavigationList>
    </NavigationSectionLayout>
  );
};
